<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import ReportDrilldownCard from 'dashboard/routes/dashboard/settings/reports/components/ReportDrilldownCard.vue';
import liveReports from 'dashboard/api/liveReports';

const props = defineProps({
  open: { type: Boolean, default: false },
  metric: { type: String, default: '' },
  metricName: { type: String, default: '' },
  teamId: { type: [String, Number], default: null },
  minutes: { type: Number, default: 15 },
});

const emit = defineEmits(['close']);
const { t } = useI18n();
const drawerRef = ref(null);
const records = ref([]);
const meta = ref({});
const isFetching = ref(false);
const isFetchingMore = ref(false);
const hasError = ref(false);
let controller = null;
let previousActiveElement = null;

const hasMore = computed(
  () => records.value.length < (meta.value.total_count || 0)
);

const rememberActiveElement = () => {
  previousActiveElement =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
};

const restoreFocus = () => {
  if (previousActiveElement?.isConnected) previousActiveElement.focus();
  previousActiveElement = null;
};

const abortRequest = () => {
  controller?.abort();
  controller = null;
};

const fetchConversations = async (page = 1) => {
  abortRequest();
  controller = new AbortController();
  if (page === 1) isFetching.value = true;
  else isFetchingMore.value = true;
  hasError.value = false;

  try {
    const response = await liveReports.getConversations({
      metric: props.metric,
      teamId: props.teamId,
      minutes: props.minutes,
      page,
      signal: controller.signal,
    });
    meta.value = response.data.meta || {};
    records.value =
      page === 1
        ? response.data.payload || []
        : [...records.value, ...(response.data.payload || [])];
  } catch (error) {
    if (error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
      hasError.value = true;
    }
  } finally {
    isFetching.value = false;
    isFetchingMore.value = false;
  }
};

const close = () => {
  abortRequest();
  emit('close');
  restoreFocus();
};

const loadMore = () => {
  if (!hasMore.value || isFetching.value || isFetchingMore.value) return;
  fetchConversations((meta.value.current_page || 1) + 1);
};

const onKeydown = event => {
  if (!props.open) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    close();
  }
};

watch(
  () => [props.open, props.metric, props.teamId, props.minutes],
  ([isOpen]) => {
    if (!isOpen) {
      abortRequest();
      restoreFocus();
      return;
    }

    rememberActiveElement();
    fetchConversations();
    nextTick(() => drawerRef.value?.focus());
  },
  { immediate: true }
);

window.addEventListener('keydown', onKeydown);
onBeforeUnmount(() => {
  abortRequest();
  window.removeEventListener('keydown', onKeydown);
  restoreFocus();
});
</script>

<template>
  <TeleportWithDirection to="body">
    <Transition name="report-drilldown-fade">
      <div
        v-if="open"
        class="fixed inset-0 z-50 bg-black/30"
        role="presentation"
        @click.self="close"
      >
        <aside
          ref="drawerRef"
          class="fixed inset-y-0 end-0 flex w-full max-w-xl flex-col bg-n-solid-1 shadow-xl outline outline-1 outline-n-container"
          role="dialog"
          aria-modal="true"
          :aria-label="metricName"
          tabindex="-1"
        >
          <header
            class="flex items-start justify-between gap-4 border-b border-n-weak px-6 py-5"
          >
            <div class="min-w-0">
              <h2 class="text-base font-medium text-n-slate-12">
                {{ metricName }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-10">
                {{
                  t('OPERATIONS.DRAWER.SUBTITLE', {
                    count: meta.total_count || 0,
                    minutes,
                  })
                }}
              </p>
            </div>
            <Button
              icon="i-lucide-x"
              slate
              ghost
              size="sm"
              :aria-label="t('OPERATIONS.DRAWER.CLOSE')"
              @click="close"
            />
          </header>

          <div class="flex-1 overflow-y-auto px-6 py-5">
            <div v-if="isFetching" class="flex justify-center py-12">
              <Spinner />
            </div>
            <div
              v-else-if="hasError"
              class="py-12 text-center text-sm text-n-ruby-11"
            >
              {{ t('OPERATIONS.DRAWER.ERROR') }}
            </div>
            <div
              v-else-if="!records.length"
              class="py-12 text-center text-sm text-n-slate-10"
            >
              {{ t('OPERATIONS.DRAWER.EMPTY') }}
            </div>
            <div v-else class="flex flex-col gap-3">
              <ReportDrilldownCard
                v-for="record in records"
                :key="record.id"
                :record="{
                  record_type: 'conversation',
                  conversation: record.conversation || record,
                }"
              />
              <Button
                v-if="hasMore"
                outline
                slate
                :is-loading="isFetchingMore"
                :label="t('OPERATIONS.DRAWER.LOAD_MORE')"
                @click="loadMore"
              />
            </div>
          </div>
        </aside>
      </div>
    </Transition>
  </TeleportWithDirection>
</template>
